import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ArrowLeft, History, Loader2, Search, RefreshCw, AlertTriangle, ShieldCheck,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { canAccessTab, ROLE_LABELS } from './adminAccessMatrix';
import { fmtServerDateTime } from './utils/datetime';
import { AuditLogExportDialog } from './audit/AuditLogExportDialog';

const PAGE_SIZE = 25;

function fmt(iso) {
  if (!iso) return '—';
  return fmtServerDateTime(iso, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/**
 * User Impersonation Audit Logs — standalone page surfaced from the
 * Support module of the admin dashboard. Backed by the immutable
 * `/admin/impersonation/audit-logs` endpoint; SuperAdmin-only by design
 * (mirrors the access matrix entry `user_audit_logs`).
 *
 * UX parity with `AdminUserConsolePage`: dark theme, amber accent, same
 * table styling, plus a client-side search filter that scans admin /
 * target / org / purpose / IP cells of the currently loaded page.
 */
export function AdminUserAuditLogsPage() {
  const { admin, loading } = useAdminAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [fetching, setFetching] = useState(true);

  // CSV export dialog state.
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [period, setPeriod] = useState('yearly'); // yearly | quarterly | monthly
  const currentYear = new Date().getUTCFullYear();
  const currentMonth = new Date().getUTCMonth() + 1; // 1..12
  const [yearVal, setYearVal] = useState(String(currentYear));
  const [quarterVal, setQuarterVal] = useState(String(Math.floor((currentMonth - 1) / 3) + 1));
  const [monthVal, setMonthVal] = useState(String(currentMonth));

  // Year range: current year back 5 years so most compliance asks resolve in one click.
  const yearOptions = useMemo(
    () => Array.from({ length: 6 }, (_, i) => String(currentYear - i)),
    [currentYear],
  );

  const load = useCallback(async (p = 1) => {
    setFetching(true);
    try {
      const r = await adminApi.get('/admin/impersonation/audit-logs', {
        params: { page: p, page_size: PAGE_SIZE },
      });
      setLogs(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setPage(p);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load audit logs');
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (admin) load(1);
  }, [admin, load]);

  const runExport = async () => {
    setExporting(true);
    try {
      const params = { period, year: yearVal };
      if (period === 'quarterly') params.quarter = quarterVal;
      if (period === 'monthly') params.month = monthVal;
      const r = await adminApi.get('/admin/impersonation/audit-logs/export', {
        params,
        responseType: 'blob',
      });
      // Pull filename out of Content-Disposition; fallback to a sensible default.
      const cd = r.headers?.['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const fallback =
        period === 'yearly'
          ? `user-impersonation-audit_${yearVal}.csv`
          : period === 'quarterly'
          ? `user-impersonation-audit_${yearVal}-Q${quarterVal}.csv`
          : `user-impersonation-audit_${yearVal}-${String(monthVal).padStart(2, '0')}.csv`;
      const filename = m ? m[1] : fallback;

      const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const rowCount = r.headers?.['x-audit-row-count'];
      toast.success(
        rowCount
          ? `Downloaded ${filename} · ${rowCount} ${Number(rowCount) === 1 ? 'entry' : 'entries'}`
          : `Downloaded ${filename}`,
      );
      setExportOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not export audit logs');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  if (!canAccessTab(admin, 'user_audit_logs')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white border border-slate-200 rounded-xl p-8 max-w-md text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
          <h2 className="text-base font-semibold text-slate-900 mb-1">Access denied</h2>
          <p className="text-sm text-slate-600 mb-4">
            User Impersonation Audit Logs are restricted to SuperAdmin. Support and
            Technical roles can write entries (by triggering an impersonation) but
            cannot read the history — mirroring real liability-audit setups.
          </p>
          <Link to="/admin">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Client-side filter over the currently loaded page. The dataset is
  // small and append-only, so a server round-trip per keystroke would be
  // overkill — page-local filtering matches the rest of the admin panel.
  const q = search.trim().toLowerCase();
  const filtered = q
    ? logs.filter((l) =>
        [l.admin_email, l.target_email, l.target_organization_name,
         l.purpose, l.ip_address, ROLE_LABELS[l.admin_role] || l.admin_role]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      )
    : logs;

  return (
    <div
      data-testid="admin-user-audit-logs-page"
      className="min-h-screen bg-slate-950 text-slate-100"
    >
      <div className="border-b border-slate-800 bg-slate-900/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-slate-400 hover:text-slate-200" data-testid="audit-logs-back">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <ShieldCheck className="w-6 h-6 text-amber-400" />
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <History className="w-4 h-4 text-amber-300" /> User Impersonation Audit Logs
              </h1>
              <p className="text-xs text-slate-400">
                Immutable record of every impersonation session — who, when, where, and why.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => setExportOpen(true)}
              data-testid="audit-logs-export-btn"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-medium"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={fetching}
              onClick={() => load(page)}
              data-testid="audit-logs-refresh"
              className="bg-slate-800 border-slate-700 text-slate-100 hover:bg-slate-700"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${fetching ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-100">
          <strong>Append-only by design.</strong> Every row was written before the
          impersonation token was returned to the admin — no token can escape without
          a paper trail. There is no UI or API to edit or delete entries here.
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter this page by admin, target, org, purpose, IP…"
            className="pl-9 bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500"
            data-testid="audit-logs-search"
          />
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-slate-400 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">When</th>
                <th className="text-left px-4 py-2.5 font-semibold">Admin</th>
                <th className="text-left px-4 py-2.5 font-semibold">Target user</th>
                <th className="text-left px-4 py-2.5 font-semibold">Purpose</th>
                <th className="text-left px-4 py-2.5 font-semibold">IP</th>
              </tr>
            </thead>
            <tbody>
              {fetching && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                </td></tr>
              )}
              {!fetching && filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  {q ? 'No entries on this page match the filter.' : 'No audit entries yet.'}
                </td></tr>
              )}
              {!fetching && filtered.map((l) => (
                <tr key={l.id} className="border-t border-slate-800 hover:bg-slate-800/40 transition-colors align-top" data-testid={`audit-row-${l.id}`}>
                  <td className="px-4 py-2.5 text-xs text-slate-400 font-mono whitespace-nowrap">{fmt(l.created_at)}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-100">{l.admin_email || '—'}</div>
                    <div className="text-[10px] uppercase tracking-wider text-amber-300/70">
                      {ROLE_LABELS[l.admin_role] || l.admin_role || ''}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-slate-200">{l.target_email || '—'}</div>
                    <div className="text-[10px] text-slate-500 truncate max-w-[260px]">
                      {l.target_organization_name || l.target_organization_id || ''}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-300 italic text-xs max-w-md" title={l.purpose}>
                    {l.purpose || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-400 font-mono">{l.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="border-t border-slate-800 px-4 py-2.5 flex items-center justify-between text-xs text-slate-400">
              <span>Page {page} of {totalPages} · {total} total {!!q && `(filter active on this page)`}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1 || fetching} onClick={() => load(page - 1)} data-testid="audit-logs-prev" className="bg-slate-800 border-slate-700 text-slate-200">Prev</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages || fetching} onClick={() => load(page + 1)} data-testid="audit-logs-next" className="bg-slate-800 border-slate-700 text-slate-200">Next</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSV Export Dialog */}
      <AuditLogExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        period={period} setPeriod={setPeriod}
        yearVal={yearVal} setYearVal={setYearVal}
        quarterVal={quarterVal} setQuarterVal={setQuarterVal}
        monthVal={monthVal} setMonthVal={setMonthVal}
        yearOptions={yearOptions}
        exporting={exporting}
        onExport={runExport}
      />
    </div>
  );
}

export default AdminUserAuditLogsPage;
